import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { MessageSquare, Zap, Globe, CheckCircle2, Activity } from 'lucide-react';
import { Header } from '../components/Header';
import { SkipLink } from '../components/SkipLink';

export const Landing = () => {
  return (
    <div className="min-h-screen bg-white">
      <SkipLink />
      <div className="grain-overlay" aria-hidden="true"></div>
      <Header />

      {/* Hero Section */}
      <section 
        className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 hero-gradient"
        aria-labelledby="hero-heading"
      >
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-7"
            >
              <div className="mb-6">
                <span className="text-xs uppercase tracking-widest text-[#475569] font-medium">
                  AI Interview Engine for B2B
                </span>
              </div>
              <h1 
                id="hero-heading"
                className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mb-6" 
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                Turn Every Form Into
                <br />
                <span className="text-[#1d4ed8]">an AI Interview</span>
              </h1>
              <p className="text-lg sm:text-xl text-[#475569] mb-8 max-w-2xl">
                Replace static forms with intelligent conversations. Evenly Respond interviews your leads via AI, qualifies them in real time, and extracts structured data — so your team focuses on closing, not chasing.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/auth">
                  <Button
                    data-testid="hero-get-started-button"
                    size="lg"
                    className="rounded-full bg-[#1d4ed8] hover:bg-[#1e40af] px-8 btn-hover-lift text-base focus:outline-none focus:ring-4 focus:ring-[#1d4ed8] focus:ring-offset-2"
                    aria-label="Start your first form with Evenly Respond"
                  >
                    Start Your First Form
                    <MessageSquare className="ml-2 h-5 w-5" aria-hidden="true" />
                  </Button>
                </Link>
                <Link to="/demo">
                  <Button
                    data-testid="hero-try-demo-button"
                    variant="outline"
                    size="lg"
                    className="rounded-full border-2 border-[#1d4ed8] text-[#1d4ed8] hover:bg-[#1d4ed8] hover:text-white px-8 btn-hover-lift text-base focus:outline-none focus:ring-4 focus:ring-[#1d4ed8] focus:ring-offset-2"
                    aria-label="Try a live demo of the conversational form experience"
                  >
                    Try Live Demo
                    <Zap className="ml-2 h-5 w-5" aria-hidden="true" />
                  </Button>
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="lg:col-span-5"
            >
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1644337540803-2b2fb3cebf12?crop=entropy&cs=srgb&fm=jpg&q=85"
                  alt="Modern minimalist workspace with desk lamp and organized workspace showing a productive environment for form completion"
                  className="rounded-3xl shadow-2xl border border-[#E2E8F0]"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <main id="main-content">
        <section 
          className="py-20 px-4 sm:px-6 lg:px-8 bg-white"
          aria-labelledby="features-heading"
        >
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <span className="text-xs uppercase tracking-widest text-[#475569] font-medium">
                How It Works
              </span>
              <h2 
                id="features-heading"
                className="text-3xl sm:text-4xl font-black mt-4 mb-4" 
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                How the AI Interview Works
              </h2>
              <p className="text-lg text-[#475569] max-w-2xl mx-auto">
                Three steps to transform static forms into intelligent, lead-qualifying conversations.
              </p>
            </div>

            <div 
              className="grid grid-cols-1 md:grid-cols-3 gap-8"
              role="list"
              aria-label="Features list"
            >
              <motion.article
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className="p-8 border border-[#E2E8F0] rounded-2xl hover:border-[#1d4ed8] transition-colors"
                data-testid="feature-paste-url"
                role="listitem"
              >
                <div 
                  className="w-12 h-12 bg-[#1d4ed8] rounded-full flex items-center justify-center mb-6"
                  aria-hidden="true"
                >
                  <Zap className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  1. Import Your Form
                </h3>
                <p className="text-[#475569]">
                  Paste a Google, Microsoft, or Typeform link. We generate a shareable QR code and interview link instantly.
                </p>
              </motion.article>

              <motion.article
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                viewport={{ once: true }}
                className="p-8 border border-[#E2E8F0] rounded-2xl hover:border-[#1d4ed8] transition-colors"
                data-testid="feature-chat-ai"
                role="listitem"
              >
                <div 
                  className="w-12 h-12 bg-[#1d4ed8] rounded-full flex items-center justify-center mb-6"
                  aria-hidden="true"
                >
                  <MessageSquare className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  2. AI Interviews Your Leads
                </h3>
                <p className="text-[#475569]">
                  Respondents scan the QR or click the link. Our AI asks intelligent follow-ups, adapts in real time, and extracts structured answers.
                </p>
              </motion.article>

              <motion.article
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                viewport={{ once: true }}
                className="p-8 border border-[#E2E8F0] rounded-2xl hover:border-[#1d4ed8] transition-colors"
                data-testid="feature-auto-submit"
                role="listitem"
              >
                <div 
                  className="w-12 h-12 bg-[#1d4ed8] rounded-full flex items-center justify-center mb-6"
                  aria-hidden="true"
                >
                  <CheckCircle2 className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  3. Qualify & Analyze
                </h3>
                <p className="text-[#475569]">
                  Get AI-powered lead scores, aggregate insights, and actionable recommendations — all from a single interview.
                </p>
              </motion.article>
            </div>
          </div>
        </section>

        {/* Multi-language Section */}
        <section 
          className="py-20 px-4 sm:px-6 lg:px-8 bg-[#F8FAFC]"
          aria-labelledby="multilanguage-heading"
        >
          <div className="max-w-5xl mx-auto text-center">
            <div 
              className="w-16 h-16 bg-[#1d4ed8] rounded-2xl flex items-center justify-center mx-auto mb-6"
              aria-hidden="true"
            >
              <Globe className="h-8 w-8 text-white" />
            </div>
            <h2 
              id="multilanguage-heading"
              className="text-3xl sm:text-4xl font-black mb-6" 
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Speak Your Language, Fill Theirs
            </h2>
            <p className="text-lg text-[#475569] max-w-3xl mx-auto">
              Forms in Spanish? German? Chinese? No problem. Respond in your native language, and Evenly Respond translates and submits in the form's original language.
            </p>
          </div>
        </section>

        {/* CTA Section */}
        <section 
          className="py-20 px-4 sm:px-6 lg:px-8 bg-[#0F172A]"
          aria-labelledby="cta-heading"
        >
          <div className="max-w-4xl mx-auto text-center">
            <h2 
              id="cta-heading"
              className="text-3xl sm:text-4xl font-black text-white mb-6" 
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Ready to Interview Smarter?
            </h2>
            <p className="text-lg text-[#94A3B8] mb-8">
              Stop losing leads to boring forms. Let AI handle the conversation while you focus on closing deals.
            </p>
            <Link to="/auth">
              <Button
                data-testid="cta-get-started-button"
                size="lg"
                className="rounded-full bg-[#1d4ed8] hover:bg-[#1e40af] px-8 btn-hover-lift text-base focus:outline-none focus:ring-4 focus:ring-[#1d4ed8] focus:ring-offset-2"
                aria-label="Get started with Evenly Respond for free"
              >
                Get Started Free
              </Button>
            </Link>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer 
        className="py-12 px-4 sm:px-6 lg:px-8 bg-white border-t border-[#E2E8F0]"
        role="contentinfo"
      >
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-[#475569]">
            © 2026 Evenly Respond. AI-powered interviews for modern B2B teams.
          </p>
          <Link 
            to="/system-status" 
            className="flex items-center gap-1.5 text-sm text-[#475569] hover:text-[#1d4ed8] transition-colors"
            data-testid="footer-status-link"
          >
            <Activity size={14} />
            System Status
          </Link>
        </div>
      </footer>
    </div>
  );
};
